import * as Sentry from '@sentry/browser';

import { getExperienceId } from '~/acquisition/utils/customizedExperience';
import { CustomerDetailNames } from '~/shared/constants/customerDetailNames';
import { getBrandValue } from '~/shared/utils/brandNameHelper';
import { getProfileSizeFilters } from '~/shared/utils/getProfileSizeFilters';
import { getTldLocale } from '~/shared/utils/getTldLocale';
import { isVerifiedBot } from '~/shared/utils/isVerifiedBot';
import { getGender } from '~/shared/utils/useGender';
import {
  getLoyaltyDetails,
  getMembership,
  loadMembership,
  loadLoyaltyDetails,
  getCustomerDetail,
} from '~/techstyle-shared/react-accounts';
import { getLocale } from '~/techstyle-shared/react-intl';
import { getDirectMarketingDisposition } from '~/techstyle-shared/react-marketing';
import { getSession, getSessionDetail } from '~/techstyle-shared/redux-core';

import { getBuilderJson } from './getBuilderJson';
import { getBuilderMembershipStatus } from './getBuilderMembershipStatus';
import { getBuilderPageTargeting } from './getBuilderPageTargeting';
import { isBuilderPreview } from './isBuilderPreview';
import { loadAsyncBuilderComponents } from './loadAsyncBuilderComponents';
import { ssrPreprocessBuilderJson } from './ssrPreprocessBuilderJson';

export const getBuilderJsonSSR = async ({
  modelName,
  urlPath,
  ctx,
  query = {},
  ignoreExperienceIdTargeting = false,
}) => {
  await Promise.all([
    ctx.store.dispatch(loadMembership()),
    ctx.store.dispatch(loadLoyaltyDetails()),
  ]);

  const membership = getMembership(ctx.store.getState());
  const isPreview = isBuilderPreview(ctx.req);
  const state = ctx.store.getState();
  const { isLoggedIn } = getSession(state);
  const membershipStatus = getBuilderMembershipStatus(membership, isLoggedIn);
  const { gender } = getGender(state);
  const { locale } = getLocale(state);
  const tldLocale = getTldLocale(state);
  const disposition = getDirectMarketingDisposition(ctx.store.getState());
  const loyalty = getLoyaltyDetails(ctx.store.getState());
  const profileSizes = getProfileSizeFilters(
    ctx.store.getState().customer,
    gender,
    tldLocale
  );
  const { tld } = ctx.req?.context?.domain ?? ctx.store.getState().domain;
  let experienceId;
  const isFirstBilling = Boolean(membership?.isFirstBilling);
  const developerDefinedTargetingId = getCustomerDetail(
    ctx.store.getState(),
    CustomerDetailNames.DEVELOPER_DEFINED_TARGETING_ID
  )?.value;
  const sessionDeveloperDefinedTargetingId = getSessionDetail(
    ctx.store.getState(),
    CustomerDetailNames.DEVELOPER_DEFINED_TARGETING_ID
  )?.value;

  // Prefer the customer detail value, but fall back to the session value
  const finalDeveloperDefinedTargetingId =
    developerDefinedTargetingId || sessionDeveloperDefinedTargetingId;

  if (!ignoreExperienceIdTargeting) {
    try {
      experienceId = await getExperienceId({
        isLoggedIn,
        disposition,
        dmg: ctx.query?.dmg,
        gender: ctx.query?.gender,
        dispatch: ctx.store.dispatch,
      });
    } catch (e) {
      Sentry.captureException(e);
    }
  }

  // Create shared page options
  const commonPageOptions = {
    experienceId,
    gender,
    locale,
    membershipStatus,
    isPreview,
    loyalty,
    vipPlusPerksAvailable: membership?.vipPlusPerksAvailable,
    bottomSize: profileSizes?.size_bottom,
    topSize: profileSizes?.size_top,
    braSize: profileSizes?.size_bra,
    tld,
    isFirstBilling,
    developerDefinedTargetingId: finalDeveloperDefinedTargetingId,
  };

  // Add brand-specific size fields
  const pageOptions = getBrandValue({
    default: {
      // For Fabletics
      ...commonPageOptions,
    },
    savage: {
      // For Savage X Fenty
      ...commonPageOptions,
      membershipPrice: membership?.price?.toString() || '',
    },
  });

  const pageTargetingOptions = getBuilderPageTargeting(pageOptions);

  const builderContentParams = {
    modelName,
    urlPath,
    query,
    pageTargetingOptions,
  };

  const { builderJson, key } = await getBuilderJson(builderContentParams);
  let builderStateData;

  // this if statement is intended to only execute for SEO purposes
  const processBuilderComponentsSSR = isVerifiedBot(ctx);
  if (processBuilderComponentsSSR) {
    /**
     * Loads asynchronous builder components
     * @components (BuilderCollection)
     */
    builderStateData = await loadAsyncBuilderComponents({
      ctx,
      builderJson,
    });
  }

  builderStateData = builderStateData
    ? { ...builderStateData, modelName }
    : { modelName };

  /**
   * Preloads assets/containers
   */
  await ssrPreprocessBuilderJson(ctx, builderJson);

  return { builderJson, builderStateData, key };
};
